import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBytes } from '../format';

export const MAX_FILE_BYTES = 50 * 1024 * 1024;

const ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'webp', 'docx', 'xlsx', 'pptx', 'doc', 'xls', 'ppt'];
const MACRO_EXTENSIONS = ['docm', 'dotm', 'xlsm', 'xltm', 'xlam', 'pptm', 'potm', 'ppsm', 'ppam'];

export const FILE_ACCEPT = ALLOWED_EXTENSIONS.map((extension) => `.${extension}`).join(',');

/** The same allowlist the API applies, so most refusals are said before the upload starts. */
export function fileProblem(file: File): 'macros' | 'notAllowed' | 'tooLarge' | 'empty' | null {
  const extension = file.name.includes('.') ? (file.name.split('.').pop() ?? '').toLowerCase() : '';

  if (MACRO_EXTENSIONS.includes(extension)) {
    return 'macros';
  }

  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return 'notAllowed';
  }

  if (file.size === 0) {
    return 'empty';
  }

  return file.size > MAX_FILE_BYTES ? 'tooLarge' : null;
}

export function FileDropzone({
  id,
  label,
  file,
  disabled,
  onChange,
  required = false,
}: {
  id: string;
  label?: string;
  file: File | null;
  disabled: boolean;
  onChange: (file: File | null) => void;
  required?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  function take(candidate: File | undefined) {
    if (!candidate) {
      return;
    }

    const refusal = fileProblem(candidate);
    setProblem(refusal ? t(`qrCode.file.${refusal}`) : null);
    onChange(refusal ? null : candidate);
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (!disabled) {
      take(event.dataTransfer.files[0]);
    }
  }

  return (
    <div>
      <div
        className={`file-dropzone${dragging ? ' is-dragging' : ''}${file ? ' has-file' : ''}`}
        data-testid="file-dropzone"
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) {
            setDragging(true);
          }
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={drop}
      >
        <i className={`pi ${file ? 'pi-file-check' : 'pi-cloud-upload'} file-dropzone__icon`} aria-hidden="true" />
        {file ? (
          <span className="file-dropzone__name">
            <span className="text-truncate" title={file.name}>
              {file.name}
            </span>
            <span className="text-body-secondary small">{formatBytes(file.size, i18n.language)}</span>
          </span>
        ) : (
          <span className="file-dropzone__prompt">{t('qrCode.file.dropHere')}</span>
        )}
        <div className="file-dropzone__actions">
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={disabled} onClick={() => input.current?.click()}>
            {file ? t('qrCode.file.chooseAnother') : t('qrCode.file.choose')}
          </button>
          {file && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              disabled={disabled}
              aria-label={t('qrCode.file.remove')}
              title={t('qrCode.file.remove')}
              onClick={() => onChange(null)}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          )}
        </div>
        <input
          ref={input}
          id={id}
          type="file"
          className="visually-hidden"
          accept={FILE_ACCEPT}
          aria-label={label}
          aria-required={required ? 'true' : undefined}
          disabled={disabled}
          onChange={(event) => {
            take(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </div>
      {problem ? (
        <span className="hint text-danger" role="alert">
          {problem}
        </span>
      ) : (
        <span className="hint">{t('qrCode.file.allowed')}</span>
      )}
    </div>
  );
}
